import { useEffect, useMemo } from 'react'

import { useSearchParams } from 'react-router-dom'

import { ReplyDetectionStatus } from '@app/models'

import { ConversationsListStatus } from './types'
import { useIsStaff } from '../user/useHasRole'

export const useChatSearchParams = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const isStaff = useIsStaff()

  const getQueryStatus = (): ConversationsListStatus =>
    searchParams.get('status') === 'spam'
      ? 'spam'
      : 'dispatched'

  const getQueryReplyDetectionStatus = (): ReplyDetectionStatus | undefined => {
    const queryReplyDetectionStatus = searchParams.get('replied')

    if (queryReplyDetectionStatus === 'false') {
      return 'Unreplied'
    }

    if (queryReplyDetectionStatus === 'true') {
      return isStaff ? 'Replied' : undefined
    }

    return undefined
  }

  const getUtmTags = () => {
    const utm_source = searchParams.get('utm_source') || undefined
    const utm_medium = searchParams.get('utm_medium') || undefined
    const utm_campaign = searchParams.get('utm_campaign') || undefined
    const utm_content = searchParams.get('utm_content') || undefined
    const utm_email_reply_conversation_id =
      searchParams.get('utm_email_reply_conversation_id') || undefined
    const utm_push_reply_conversation_id =
      searchParams.get('utm_push_reply_conversation_id') || undefined

    return {
      utm_source,
      utm_medium,
      utm_campaign,
      utm_content,
      utm_email_reply_conversation_id,
      utm_push_reply_conversation_id,
    }
  }

  const getReplyFormPushReminder = () =>
    searchParams.get('ReplyFormPushReminder') || undefined

  useEffect(() => {
    const repliedParam = searchParams.get('replied')
    const hasInvalidRepliedValue =
      !!repliedParam && repliedParam !== 'true' && repliedParam !== 'false'
    const hasForbiddenRepliedValue = repliedParam === 'true' && !isStaff

    if (!hasInvalidRepliedValue && !hasForbiddenRepliedValue) return

    const params = new URLSearchParams(searchParams)
    if (hasInvalidRepliedValue || hasForbiddenRepliedValue) {
      params.delete('replied')
    }

    setSearchParams(params, { replace: true })
  }, [isStaff, searchParams, setSearchParams])

  return useMemo(() => {
    return {
      queryStatus: getQueryStatus(),
      queryReplyDetectionStatus: getQueryReplyDetectionStatus(),
      utmTags: getUtmTags(),
      replyFormPushReminder: getReplyFormPushReminder(),
    }
  }, [isStaff, searchParams])
}
